import { FontSpec } from "chart.js";
import { ChartPlugins, ChartUtils } from "@app/utils/chartUtils";
import { useTheme } from "@mui/material";
import { typographyOptions } from "@app/theme/typography";
import { getDftVersusTenYearReturnData } from "@app/components/ChartDftVersusTenYearReturn";
import { formatPercent } from "@app/utils";
import { CarPdfChart, CarPdfChartProps } from "@app/components/PdfChart";
import { CarAssetClassDftItem } from "../useAssetClassSummaryStat";

interface DftVersusTenYearReturnPdfChartProps
  extends Omit<CarPdfChartProps, "options" | "data" | "plugins" | "type"> {
  dftItem: CarAssetClassDftItem;
  isWhiteContext?: boolean;
}

// extending grid by 20 pixels an moving it into right page padding,
// in order to align roundedBackground with other elements on the page
const RIGHT_OFFSET = 20;

export const DftVersusTenYearReturnPdfChart = ({
  dftItem,
  isWhiteContext,
  ...props
}: DftVersusTenYearReturnPdfChartProps) => {
  const theme = useTheme();

  const { datasets, range, yLabel, dftEnd } = getDftVersusTenYearReturnData({
    theme,
    dftItem: dftItem,
    forPdf: true,
  });

  const font: Partial<FontSpec> = {
    size: 9,
    weight: 400,
    lineHeight: 1,
    family: typographyOptions.fontFamily,
  };

  return (
    <CarPdfChart
      type="line"
      data={{ datasets }}
      options={{
        layout: {
          padding: {
            right: RIGHT_OFFSET,
          },
        },
        scales: {
          x: {
            type: "linear",
            min: range.minX,
            max: range.maxX,
            ticks: {
              padding: 0,
              font,
              callback: (value) =>
                typeof value === "number" ? formatPercent(value, 1) : value,
            },
            grid: {
              display: false,
            },
          },
          y: {
            type: "linear",
            min: range.minY,
            max: range.maxY,
            offset: false,
            ticks: {
              padding: 0,
              font,
              color: theme.palette.softBlack,
              callback: (value, index, values) => {
                if (index === 0 || index === values.length - 1) {
                  return undefined;
                }
                return typeof value === "number"
                  ? formatPercent(value, 1)
                  : value;
              },
            },
            grid: ChartUtils.getGridOptions({ theme }),
            title: {
              display: true,
              text: yLabel,
              color: theme.palette.softBlack,
              font,
            },
          },
        },
        plugins: {
          roundedBackground: ChartUtils.getRoundedBackgroundOptions({
            theme,
            isWhiteContext,
          }),
          rangeHighlight: {
            labelPosition: "horizontal",
            borderColor: theme.palette.gray4,
            font: {
              ...font,
              size: 7,
            },
            textColor: theme.palette.softBlack,
            items: [
              {
                value: dftEnd,
                maxDelta: 5,
                minDelta: -5,
                minLabel: "DFT",
                maxLabel: "DFT",
                backgroundColor: "#fdf5ee",
              },
            ],
          },
        },
      }}
      plugins={[ChartPlugins.roundedBackground, ChartPlugins.rangeHighlight]}
      {...props}
      width={props.width + RIGHT_OFFSET}
      style={{
        ...props.style,
        marginRight: -RIGHT_OFFSET,
      }}
    />
  );
};
